export type LevelEntity = {
  x: number;
  y: number;
  tile: string;
  requiredKey?: number | null;
  itemType?: "fuel" | string;
  value?: number;
  lightType?: "lamp" | string;
  lightRadius?: number;
};

export type LevelData = {
  schemaVersion: number;
  generator: string;
  seed: number;
  difficulty: {
    key: string;
    label: string;
    mazeScale?: number;
    numRegions: number;
    playerSpeed: number;
    lightDrain: number;
    lampChanceScale?: number;
    mazeOpenness?: number;
    itemSpawn?: {
      fuel: number;
      range: number;
      coin: number;
      sonar: number;
    };
    runtime?: Record<string, unknown>;
  };
  tileSize: number;
  width: number;
  height: number;
  rows: string[][];
  entities: {
    start: LevelEntity | null;
    finish: LevelEntity | null;
    keys: LevelEntity[];
    doors: LevelEntity[];
    dealers: LevelEntity[];
    items: LevelEntity[];
    lights: LevelEntity[];
  };
  legend: {
    kinds: Record<string, string>;
    solid: string[];
  };
};

export async function loadLevel(url: string): Promise<LevelData> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Level load failed: ${response.status} ${response.statusText}`);
  }
  const level = (await response.json()) as LevelData;
  validateLevel(level);
  return level;
}

export function validateLevel(level: LevelData): void {
  if (level.schemaVersion !== 1) {
    throw new Error(`Unsupported level schema: ${level.schemaVersion}`);
  }
  if (!Array.isArray(level.rows) || level.rows.length === 0) {
    throw new Error("Level has no rows.");
  }
  if (!level.entities.start) {
    throw new Error("Level has no start entity.");
  }
}

export function tileAt(level: LevelData, x: number, y: number): string {
  if (x < 0 || y < 0 || x >= level.width || y >= level.height) {
    return "WWW";
  }
  return level.rows[y]?.[x] ?? "WWW";
}

export function isWallTile(tile: string): boolean {
  const normalized = tile.toUpperCase();
  return normalized === "WWW" || normalized.startsWith("W_") || normalized.startsWith("WW") || normalized.startsWith("KW");
}

export function isSolidTile(tile: string): boolean {
  return isWallTile(tile) || tile.startsWith("T");
}

export function tileKind(tile: string): string {
  if (isWallTile(tile)) return "wall";
  if (tile === "S_1") return "start";
  if (tile.startsWith("E_")) return "finish";
  if (tile.startsWith("KY")) return "key";
  if (tile.startsWith("T")) return "door";
  if (tile.startsWith("t")) return "fakeDoor";
  if (tile.startsWith("D_")) return "dealer";
  return "floor";
}
